import { useSelector } from 'react-redux';
import MixPanelService from '@/services/MixPanelService';
import {
  TRACK_OVERVIEW_MINI_CARD,
  TRACK_PENDING_CARD,
  TRACK_CONTEXT,
  TRACK_OVERVIEW_CARD,
  TRACK_PERFORMANCE_KPI_CARD,
  TRACK_PERFORMANCE_CARD,
  TRACK_PERFORMANCE_TABLE,
  TRACK_TAB,
} from '../constants/track';

// apply "Row" element on pending tab
export function useTrackPendingTab() {
  const isAdmin = useSelector(
    (state) => state.AuthSlice.permissions?.is_company_admin
  );

  const createTrack = (card) => (element) => {
    MixPanelService.track({
      event_name: TRACK_CONTEXT.PENDING,
      extra_info: {
        card,
        element,
        'user-type': isAdmin ? 'Admin' : 'No admin',
      },
    });
  };

  return {
    pendingEvents: createTrack(TRACK_PENDING_CARD.EVENTS_FOR_TODAY),
    unassignedInquiries: createTrack(TRACK_PENDING_CARD.UNASSIGNED_INQUIRIES),
    pendingContact: createTrack(TRACK_PENDING_CARD.CONTACTS_TO_REACH_OUT),
    portalsNotifications: createTrack(
      TRACK_PENDING_CARD.LISTINGS_PORTALS_AND_ERRORS
    ),
    pendingLeads: createTrack(TRACK_PENDING_CARD.LEADS_TO_EXPIRE),
    pendingReservations: createTrack(TRACK_PENDING_CARD.RESERVATIONS_TO_EXPIRE),
    expiringRent: createTrack(TRACK_PENDING_CARD.RENTS_TO_EXPIRE),
    visitsToConfirm: createTrack(TRACK_PENDING_CARD.VISITS_TO_CONFIRM),
    propertiesPendingReview: createTrack(
      TRACK_PENDING_CARD.PROPERTIES_PENDING_REVIEW
    ),
    developmentsPendingReview: createTrack(
      TRACK_PENDING_CARD.DEVELOPMENTS_PENDING_REVIEW
    ),
    qprecoMarketplace: createTrack(TRACK_PENDING_CARD.QPRECO_MARKETPLACE),
    marketplaceRegistrationQuality: createTrack(
      TRACK_PENDING_CARD.MARKETPLACE_REGISTRATION_QUALITY
    ),
  };
}

export function useTrackMiniCards() {
  const isAdmin = useSelector(
    (state) => state.AuthSlice.permissions?.is_company_admin
  );

  const createTrack = (minicard) => () => {
    MixPanelService.track({
      event_name: TRACK_CONTEXT.OVERVIEW_MINI_CARD,
      extra_info: {
        minicard,
        'user-type': isAdmin ? 'Admin' : 'No admin',
      },
    });
  };

  return {
    contacts: createTrack(TRACK_OVERVIEW_MINI_CARD.CONTACTS),
    properties: createTrack(TRACK_OVERVIEW_MINI_CARD.PROPERTIES),
    developments: createTrack(TRACK_OVERVIEW_MINI_CARD.DEVELOPMENTS),
  };
}

export function useTrackOverviewTab() {
  const isAdmin = useSelector(
    (state) => state.AuthSlice.permissions?.is_company_admin
  );

  const createTrack = (card) => (element) => {
    MixPanelService.track({
      event_name: TRACK_CONTEXT.OVERVIEW,
      extra_info: {
        card,
        element,
        'user-type': isAdmin ? 'Admin' : 'No admin',
      },
    });
  };

  return {
    contactSource: createTrack(TRACK_OVERVIEW_CARD.CONTACT_SOURCE),
    leads: createTrack(TRACK_OVERVIEW_CARD.LEADS),
    pendingScheduledEvents: createTrack(
      TRACK_OVERVIEW_CARD.PENDING_SCHEDULED_EVENTS
    ),
    visits: createTrack(TRACK_OVERVIEW_CARD.VISITS),
    propertyTotalValue: createTrack(TRACK_OVERVIEW_CARD.PROPERTY_TOTAL_VALUE),
    propertyOperation: createTrack(TRACK_OVERVIEW_CARD.PROPERTY_OPERATION),
    propertyType: createTrack(TRACK_OVERVIEW_CARD.PROPERTY_TYPE),
    propertyLocation: createTrack(TRACK_OVERVIEW_CARD.PROPERTY_LOCATION),
    propertyConstructionAge: createTrack(
      TRACK_OVERVIEW_CARD.PROPERTY_CONSTRUCTION_AGE
    ),
    propertyEntryDate: createTrack(TRACK_OVERVIEW_CARD.PROPERTY_ENTRY_DATE),
    propertyLastUpdate: createTrack(TRACK_OVERVIEW_CARD.PROPERTY_LAST_UPDATE),
    contactLastUpdate: createTrack(TRACK_OVERVIEW_CARD.CONTACT_LAST_UPDATE),
    developmentType: createTrack(TRACK_OVERVIEW_CARD.DEVELOPMENT_TYPE),
    developmentLocation: createTrack(TRACK_OVERVIEW_CARD.DEVELOPMENT_LOCATION),
  };
}

export function useTrackKPICards() {
  const isAdmin = useSelector(
    (state) => state.AuthSlice.permissions?.is_company_admin
  );

  const createTrack = (minicard) => () => {
    MixPanelService.track({
      event_name: TRACK_CONTEXT.PERFORMANCE_KPI_CARD,
      extra_info: {
        minicard,
        'user-type': isAdmin ? 'Admin' : 'No admin',
      },
    });
  };

  return {
    inquiriesReceived: createTrack(
      TRACK_PERFORMANCE_KPI_CARD.INQUIRIES_RECEIVED
    ),
    newContacts: createTrack(TRACK_PERFORMANCE_KPI_CARD.NEW_CONTACTS),
    responseTime: createTrack(TRACK_PERFORMANCE_KPI_CARD.RESPONSE_TIME),
    visitsMade: createTrack(TRACK_PERFORMANCE_KPI_CARD.VISITS_MADE),
    conversionRate: createTrack(TRACK_PERFORMANCE_KPI_CARD.CONVERSION_RATE),
    conversionTime: createTrack(TRACK_PERFORMANCE_KPI_CARD.CONVERSION_TIME),
    addedProperties: createTrack(TRACK_PERFORMANCE_KPI_CARD.ADDED_PROPERTIES),
    concretedOperations: createTrack(
      TRACK_PERFORMANCE_KPI_CARD.CONCRETED_OPERATIONS
    ),
  };
}

export function useTrackPerformanceTabCard() {
  const isAdmin = useSelector(
    (state) => state.AuthSlice.permissions?.is_company_admin
  );

  const createTrack = (card) => (element) => {
    MixPanelService.track({
      event_name: TRACK_CONTEXT.PERFORMANCE_CARD,
      extra_info: {
        card,
        element,
        'user-type': isAdmin ? 'Admin' : 'No admin',
      },
    });
  };

  return {
    inquiriesReceived: createTrack(TRACK_PERFORMANCE_CARD.INQUIRIES_RECEIVED),
    newContacts: createTrack(TRACK_PERFORMANCE_CARD.NEW_CONTACTS),
    mostInquiriedProperties: createTrack(
      TRACK_PERFORMANCE_CARD.MOST_INQUIRIES_PROPERTIES
    ),
    leastInquiriedProperties: createTrack(
      TRACK_PERFORMANCE_CARD.LEAST_INQUIRIES_PROPERTIES
    ),
    mostInquiriedDevelopments: createTrack(
      TRACK_PERFORMANCE_CARD.MOST_INQUIRIES_DEVELOPMENTS
    ),
    leastInquiriedDevelopments: createTrack(
      TRACK_PERFORMANCE_CARD.LEAST_INQUIRIES_DEVELOPMENTS
    ),
    medianResponseTime: createTrack(
      TRACK_PERFORMANCE_CARD.MEDIAN_RESPONSE_TIME
    ),
    responseTimeEvolution: createTrack(
      TRACK_PERFORMANCE_CARD.RESPONSE_TIME_EVOLUTION
    ),
    visitsMade: createTrack(TRACK_PERFORMANCE_CARD.VISITS_MADE),
    visitsMadeEvolution: createTrack(
      TRACK_PERFORMANCE_CARD.VISITS_MADE_EVOLUTION
    ),
    eventsMade: createTrack(TRACK_PERFORMANCE_CARD.EVENTS_MADE),
    reservationsCreated: createTrack(
      TRACK_PERFORMANCE_CARD.RESERVATIONS_CREATED
    ),
    closedLeads: createTrack(TRACK_PERFORMANCE_CARD.CLOSED_LEADS),
    conversionRate: createTrack(TRACK_PERFORMANCE_CARD.CONVERSION_RATE),
    medianConversionTime: createTrack(
      TRACK_PERFORMANCE_CARD.MEDIAN_CONVERSION_TIME
    ),
    productionRate: createTrack(TRACK_PERFORMANCE_CARD.PRODUCTION_RATE),
    addedProperties: createTrack(TRACK_PERFORMANCE_CARD.ADDED_PROPERTIES),
    concretedOperations: createTrack(
      TRACK_PERFORMANCE_CARD.CONCRETED_OPERATIONS
    ),
    concretedOperationsSource: createTrack(
      TRACK_PERFORMANCE_CARD.CONCRETED_OPERATIONS_SOURCE
    ),
  };
}

export function useTrackDashboardTabs() {
  const isAdmin = useSelector(
    (state) => state.AuthSlice.permissions?.is_company_admin
  );

  const createTrack = (tab) => () => {
    MixPanelService.track({
      event_name: TRACK_CONTEXT.TABS,
      extra_info: {
        tab,
        'user-type': isAdmin ? 'Admin' : 'No admin',
      },
    });
  };
  return {
    pending: createTrack(TRACK_TAB.PENDING),
    overview: createTrack(TRACK_TAB.OVERVIEW),
    performance: createTrack(TRACK_TAB.PERFORMANCE),
  };
}

export default function useTrackPerformanceTable() {
  const isAdmin = useSelector(
    (state) => state.AuthSlice.permissions?.is_company_admin
  );

  const createTrack = (card) => (element) => {
    MixPanelService.track({
      event_name: TRACK_CONTEXT.PERFORMANCE_TABLE,
      extra_info: {
        card,
        element,
        'user-type': isAdmin ? 'Admin' : 'No admin',
      },
    });
  };
  return {
    inquiriesReceived: createTrack(TRACK_PERFORMANCE_TABLE.INQUIRIES_RECEIVED),
    newContacts: createTrack(TRACK_PERFORMANCE_TABLE.NEW_CONTACTS),
    responseTime: createTrack(TRACK_PERFORMANCE_TABLE.RESPONSE_TIME),
    doneVisits: createTrack(TRACK_PERFORMANCE_TABLE.DONE_VISITS),
    doneEvents: createTrack(TRACK_PERFORMANCE_TABLE.DONE_EVENTS),
    conversionRate: createTrack(TRACK_PERFORMANCE_TABLE.CONVERSION_RATE),
    conversionTime: createTrack(TRACK_PERFORMANCE_TABLE.CONVERSION_TIME),
    addedProperties: createTrack(TRACK_PERFORMANCE_TABLE.ADDED_PROPERTIES),
    concretedOperations: createTrack(
      TRACK_PERFORMANCE_TABLE.CONCRETED_OPERATIONS
    ),
    propertyConsultations: createTrack(
      TRACK_PERFORMANCE_TABLE.PROPERTY_CONSULTATIONS
    ),
    developmentConsultations: createTrack(
      TRACK_PERFORMANCE_TABLE.DEVELOPMENT_CONSULTATIONS
    ),
    closedLeads: createTrack(TRACK_PERFORMANCE_CARD.CLOSED_LEADS),
    reservationsCreated: createTrack(
      TRACK_PERFORMANCE_CARD.RESERVATIONS_CREATED
    ),
    productionRate: createTrack(TRACK_PERFORMANCE_CARD.PRODUCTION_RATE),
    agentsPerformance: createTrack(TRACK_PERFORMANCE_CARD.AGENTS_PERFORMANCE),
  };
}
